import { useCallback, useEffect, useRef, useState } from 'react';
import { useHostAuth } from '../../providers/host-auth-context';
import {
  deleteThread as deleteThreadRequest,
  fetchThreadHistory,
  fetchThreadList,
  newMessageId,
  newThreadId,
  streamChat,
} from './chat-client';
import { applyStreamEvent, finishMessage } from './stream-events';
import type { ChatMessage, ChatThread, ThreadSummary } from './types';

/**
 * The conversation: the thread on screen, the list of earlier ones, and the
 * requests that move between them.
 *
 * The Angular service this replaces held the thread in a `BehaviorSubject`
 * that every caller mutated in place — the streaming path pushed onto
 * `messages` and re-emitted the same object, so nothing could tell one frame
 * from the next. It is ordinary state here, replaced rather than mutated, and
 * the stream folds onto it through `applyStreamEvent`.
 */

interface UseChat {
  thread: ChatThread | null;
  isSending: boolean;
  threads: ThreadSummary[];
  isLoadingThreads: boolean;
  error: string | null;
  startNewThread: () => void;
  sendMessage: (message: string) => void;
  loadThreads: () => void;
  openThread: (threadId: string) => void;
  removeThread: (threadId: string) => void;
}

export function useChat(): UseChat {
  const { getAccessToken } = useHostAuth();
  const [thread, setThread] = useState<ChatThread | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [threads, setThreads] = useState<ThreadSummary[]>([]);
  const [isLoadingThreads, setIsLoadingThreads] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Every request is dropped on unmount: a reply can take a while, and the
  // widget goes away with the page when someone signs out mid-answer.
  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    abortRef.current = controller;
    return () => controller.abort();
  }, []);

  const startNewThread = useCallback(() => {
    setError(null);
    setThread({ id: newThreadId(), messages: [] });
  }, []);

  const sendMessage = useCallback(
    (text: string) => {
      const message = text.trim();
      if (!message || isSending) return;

      setError(null);
      setIsSending(true);

      const userMessage: ChatMessage = {
        id: newMessageId(),
        content: message,
        role: 'user',
        timestamp: new Date().toISOString(),
      };
      const assistantMessage: ChatMessage = {
        id: newMessageId(),
        content: '',
        role: 'assistant',
        timestamp: new Date().toISOString(),
        isLoading: true,
      };

      // Decided here rather than read back from state, which is not readable
      // straight after setting it — the request has to carry the same id the
      // placeholder is appended to. The very first message of a session has no
      // thread yet, and the service replaces this one with its own in the
      // metadata frame anyway.
      const threadId = thread?.id ?? newThreadId();
      setThread((current) => {
        const base = current ?? { id: threadId, messages: [] };
        return { ...base, messages: [...base.messages, userMessage, assistantMessage] };
      });

      const fold = (event: Parameters<typeof applyStreamEvent>[2]) => {
        setThread((current) =>
          current ? applyStreamEvent(current, assistantMessage.id, event, message) : current,
        );
      };

      streamChat(message, threadId, getAccessToken, fold, abortRef.current?.signal)
        .then(() => {
          setThread((current) => (current ? finishMessage(current, assistantMessage.id) : current));
        })
        .catch((cause) => {
          if (cause?.name === 'AbortError') return;
          setThread((current) =>
            current
              ? finishMessage(
                  current,
                  assistantMessage.id,
                  'Sorry, I encountered an error while processing your request.',
                )
              : current,
          );
        })
        .finally(() => setIsSending(false));
    },
    [getAccessToken, isSending, thread?.id],
  );

  const loadThreads = useCallback(() => {
    setIsLoadingThreads(true);
    setError(null);
    fetchThreadList(getAccessToken, {}, abortRef.current?.signal)
      .then((response) => setThreads(response.threads))
      .catch((cause) => {
        if (cause?.name === 'AbortError') return;
        setError('Your previous conversations could not be loaded.');
      })
      .finally(() => setIsLoadingThreads(false));
  }, [getAccessToken]);

  const openThread = useCallback(
    (threadId: string) => {
      setIsSending(true);
      setError(null);
      fetchThreadHistory(threadId, getAccessToken, abortRef.current?.signal)
        .then(setThread)
        .catch((cause) => {
          if (cause?.name === 'AbortError') return;
          setError('That conversation could not be opened.');
        })
        .finally(() => setIsSending(false));
    },
    [getAccessToken],
  );

  const removeThread = useCallback(
    (threadId: string) => {
      // Taken off the list first: the row is gone from under the pointer
      // either way, and putting it back on failure reads as a bug rather than
      // as an error. The banner says what happened.
      setThreads((current) => current.filter((summary) => summary.thread_id !== threadId));
      deleteThreadRequest(threadId, getAccessToken)
        .then(() => {
          setThread((current) => (current?.id === threadId ? null : current));
        })
        .catch(() => setError('That conversation could not be deleted.'));
    },
    [getAccessToken],
  );

  return {
    thread,
    isSending,
    threads,
    isLoadingThreads,
    error,
    startNewThread,
    sendMessage,
    loadThreads,
    openThread,
    removeThread,
  };
}
